import React, { useCallback, useState } from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SearchTextField } from 'src/components/BeaconV2Components/SearchInput/SearchInputBar';
import {
  SlDialog,
  SlDialogCloseButton,
  SlDialogHeader,
  SlDialogTitle,
  SlDialogContent,
  SlDialogFooter,
  SlDialogRoot
} from 'src/components/BeaconV2Components/SlDialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Divider } from '@mui/material';
import { SlCheckbox, SlCheckboxContainer, SlCheckboxLabel } from 'src/components/BeaconV2Components/SlCheckbox';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { topicSettingsServerProxy } from '../serverProxy';
import { SlShowMore } from 'src/components/BeaconV2Components/SlShowMore/SlShowMore';
import { TopicGroup } from '../../types';
import ScrollableContainer from 'src/components/BeaconV2Components/ScrollableContainer';
import { useMainEntity, useUserProfile } from 'src/utils/Hooks/reduxSelectors';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton';
import { useSnackbar } from 'src/utils/Hooks';
import { GenericErrorMessage } from 'src/components/BeaconV2Components/common/CommonSnackbarMessages/GenericErrorMessage';
import pluralize from 'pluralize';
import { SlTripleFolderIcon } from 'src/components/SvgIcons';
import { TOPIC_QUERY_KEYS } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/topicQueryKeys';
import { useAddTopicGroupStoreActions } from 'src/zustandStores/BeaconPro/Topics/TopicGroups/useAddTopicGroupStore';
import { useEditTopicGroupContext } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/TopicGroupInclusions/TopicGroupInclusionProvider';

import { useTopicPolling } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/hooks/useTopicPolling';
import { useTopicTaxonomyOptions } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/hooks/useTopicTaxonomyOptions';

/**
 * Hook that exposes a mutation function for adding or removing the topic from multiple topic groups via the Topic Group Inclusions section.
 */
const useTopicSettingsMutation = () => {
  const queryClient = useQueryClient();
  const taxonomyOptions = useTopicTaxonomyOptions();

  const { pollMultipleTopicGroupsUpdate } = useTopicPolling();

  return useMutation({
    mutationFn: async ({
      topicId,
      selectedTopicGroupIds,
      originalTopicGroupIds
    }: {
      topicId: string;
      selectedTopicGroupIds: string[];
      originalTopicGroupIds: string[];
    }) => {
      const topicGroupIdsToRemoveFrom = originalTopicGroupIds.filter((id) => !selectedTopicGroupIds.includes(id));
      const topicGroupIdsToAddTo = selectedTopicGroupIds.filter((id) => !originalTopicGroupIds.includes(id));

      if (topicGroupIdsToRemoveFrom.length) {
        await topicSettingsServerProxy.removeTopicFromMultipleTopicGroups({
          topicId,
          topicGroupIds: topicGroupIdsToRemoveFrom,
          taxonomyOptions
        });
      }

      if (topicGroupIdsToAddTo.length) {
        await topicSettingsServerProxy.addTopicToTopicGroups({
          topicId,
          topicGroupIds: topicGroupIdsToAddTo,
          taxonomyOptions
        });
      }

      // Return topic group IDs for onSuccess callback
      return [...topicGroupIdsToRemoveFrom, ...topicGroupIdsToAddTo];
    },

    onSuccess: (topicGroupIds) => {
      queryClient.invalidateQueries([TOPIC_QUERY_KEYS.all]);
      pollMultipleTopicGroupsUpdate(topicGroupIds, 'edit-topic-inclusions');
    }
  });
};

/**
 * On the Traffic Settings page for the topics view, this modal allows the user to add the current topic to a topic group.
 */
export function EditGroupInclusionsModal() {
  const { modalOpen, setModalOpen, selectedTopicGroupIds, setSelectedTopicGroupIds, originalTopicGroupIds } =
    useEditTopicGroupContext();

  const mainEntity = useMainEntity();
  const topicId = mainEntity?.topicId;

  const { data, isLoading } = useQuery({
    queryKey: [TOPIC_QUERY_KEYS.all],
    queryFn: topicSettingsServerProxy.getOwnedTopicGroups
  });

  const { firstName, lastName } = useUserProfile();
  const { showSnackbar } = useSnackbar();

  const [topicGroupSearch, setTopicGroupSearch] = useState('');

  const { openModal: openTopicGroupModal } = useAddTopicGroupStoreActions();

  const noGroupsAvailable = !isLoading && data?.length === 0;

  const { mutateAsync: editTopicSettings, isLoading: saveLoading } = useTopicSettingsMutation();

  const handleClear = useCallback(() => {
    setSelectedTopicGroupIds([]);
    setTopicGroupSearch('');
  }, [setSelectedTopicGroupIds]);

  const handleSave = useCallback(async () => {
    if (saveLoading) {
      return;
    }

    try {
      await editTopicSettings({
        topicId,
        selectedTopicGroupIds,
        originalTopicGroupIds
      });

      showSnackbar({
        type: 'success',
        message: `You've successfully added this topic to ${selectedTopicGroupIds.length} topic ${pluralize(
          'group',
          selectedTopicGroupIds.length
        )}.`
      });
    } catch (error) {
      showSnackbar({
        type: 'error',
        message: <GenericErrorMessage />
      });
    } finally {
      setModalOpen(false);
    }
  }, [
    saveLoading,
    selectedTopicGroupIds,
    topicId,
    editTopicSettings,
    showSnackbar,
    setModalOpen,
    originalTopicGroupIds
  ]);

  const renderContent = useCallback(() => {
    if (noGroupsAvailable) {
      return (
        <Flex marginTop="64px" justifyContent="center">
          <Flex gap="sm" flexDirection="column">
            <Flex gap={20} flexDirection="column" alignItems="start">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '60px',
                  height: '60px',
                  borderRadius: '100px',
                  backgroundColor: '#f6f7f8'
                }}
              >
                <SlTripleFolderIcon style={{ width: '32px' }} />
              </div>
              <Flex flexDirection="column" gap={8} alignItems="start">
                <Text sx={{ fontWeight: 'bold', fontSize: '20px' }} variant="subtitle1">
                  No topic groups found
                </Text>
                <div
                  style={{
                    maxWidth: '264px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px'
                  }}
                >
                  <Text variant="body1">It looks like you haven’t created any topic groups yet.</Text>
                  <Text variant="body1">
                    You must first create a topic group and then you can assign this topic to it thereafter.
                  </Text>
                </div>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      );
    }

    return (
      <>
        <Text variant="subtitle1">Topic Groups</Text>
        <SearchTextField
          value={topicGroupSearch}
          onChange={(e) => setTopicGroupSearch(e.target.value)}
          placeholder="Search topic groups"
          sx={{
            marginTop: '16px'
          }}
        />
        <Flex
          sx={{
            paddingLeft: '20px',
            marginTop: '32px'
          }}
        >
          <Text
            sx={{
              width: '460px',
              fontSize: '12px',
              fontWeight: 'bold'
            }}
            transform="uppercase"
          >
            Topic Group
          </Text>
          <Text
            sx={{
              fontSize: '12px',
              fontWeight: 'bold'
            }}
            transform="uppercase"
          >
            Owner
          </Text>
        </Flex>
        <Divider
          sx={{
            marginTop: '16px'
          }}
        />
        {isLoading ? (
          <Flex flexDirection="column" gap="md" marginTop="16px">
            {Array.from({ length: 7 }).map((_, i) => (
              <Flex key={i} gap={180}>
                <SlSkeleton width="300px" height="24px" />
                <SlSkeleton width="100px" height="24px" />
              </Flex>
            ))}
          </Flex>
        ) : (
          <ScrollableContainer
            sx={{
              height: '300px'
            }}
          >
            <SlShowMore
              itemsPerChunk={20}
              items={
                data?.filter((topicGroup) =>
                  topicGroup.topicGroupName.toLowerCase().includes(topicGroupSearch.toLowerCase())
                ) ?? []
              }
              renderItems={(items) =>
                items.map((topicGroup: TopicGroup) => (
                  <Flex
                    key={topicGroup.topicGroupId}
                    sx={{
                      marginTop: '16px'
                    }}
                  >
                    <SlCheckboxContainer
                      sx={{
                        width: '480px'
                      }}
                    >
                      <SlCheckbox
                        id={topicGroup.topicGroupId.toString()}
                        checked={selectedTopicGroupIds.includes(topicGroup.topicGroupId)}
                        onChange={(e) =>
                          e.target.checked
                            ? setSelectedTopicGroupIds([...selectedTopicGroupIds, topicGroup.topicGroupId])
                            : setSelectedTopicGroupIds(
                                selectedTopicGroupIds.filter((id) => id !== topicGroup.topicGroupId)
                              )
                        }
                      />
                      <SlCheckboxLabel htmlFor={topicGroup.topicGroupId.toString()}>
                        {topicGroup.topicGroupName}
                      </SlCheckboxLabel>
                    </SlCheckboxContainer>
                    <Text variant="body2">{`${firstName} ${lastName}`}</Text>
                  </Flex>
                ))
              }
            />
          </ScrollableContainer>
        )}
      </>
    );
  }, [
    isLoading,
    data,
    topicGroupSearch,
    selectedTopicGroupIds,
    firstName,
    lastName,
    noGroupsAvailable,
    setSelectedTopicGroupIds
  ]);

  const renderButtons = useCallback(() => {
    if (noGroupsAvailable) {
      return (
        <>
          <SlButton onClick={() => setModalOpen(false)}>Cancel</SlButton>
          <SlButton
            onClick={() => {
              setModalOpen(false);
              openTopicGroupModal();
            }}
            variant="contained"
          >
            Create Topic Group
          </SlButton>
        </>
      );
    }

    return (
      <>
        <SlButton onClick={handleClear}>Clear</SlButton>
        <SlButton onClick={handleSave} variant="contained" disabled={selectedTopicGroupIds.length === 0 || saveLoading}>
          {saveLoading ? 'Saving...' : 'Save'}
        </SlButton>
      </>
    );
  }, [
    noGroupsAvailable,
    saveLoading,
    selectedTopicGroupIds,
    handleSave,
    handleClear,
    openTopicGroupModal,
    setModalOpen
  ]);

  return (
    <SlDialogRoot>
      <SlDialog open={modalOpen} onClose={() => setModalOpen(false)} width="752px" height="641px">
        <SlDialogHeader>
          <SlDialogTitle>Edit Inclusions</SlDialogTitle>
          <SlDialogCloseButton onClick={() => setModalOpen(false)} />
        </SlDialogHeader>
        <SlDialogContent
          sx={{
            paddingRight: '32px'
          }}
        >
          {renderContent()}
        </SlDialogContent>
        <SlDialogFooter>
          <Flex justifyContent="space-between">{renderButtons()}</Flex>
        </SlDialogFooter>
      </SlDialog>
    </SlDialogRoot>
  );
}
